<template>
    <div class="container">
        <h1 class="my-3">{{ $t('nav.buildings') }}</h1>

        <div class="input-group mb-3 rounded-pill">
            <input type="text" class="form-control" :placeholder="$t('browse.searchRealestates')" v-model="searchTerm"
                @keyup.enter.prevent="onSearchSubmit" />
            <span class="input-group-text bg-white" type="button" @click="onSearchSubmit">
                <i class="fa-solid fa-magnifying-glass"></i>
            </span>
        </div>

        <FilterGroups :filterGroups="filterGroups" @filters-changed="onFiltersChanged" />

        <div v-if="realestates.length > 0">
            <ul class="list-group mb-3">
                <li v-for="realestate in realestates" :key="realestate.id" class="list-group-item p-3">
                    <router-link :to="`/realestates/${realestate.id}`" class="text-decoration-none">
                        <div class="row align-items-center">
                            <div class="col">
                                <div class="fw-bolder small">{{ realestate.name }}</div>
                                <div v-if="realestate.name != realestate.address" class="small">{{ realestate.address }}
                                </div>
                            </div>
                            <div class="col-auto text-end">
                                <button class="btn-icon"><i class="fa-solid fa-arrow-right"></i></button>
                            </div>
                        </div>
                    </router-link>
                </li>
            </ul>

            <ListPagination :currentPage="currentPage" :totalTasks="totalItems" :limit="limit"
                @page-changed="onPageChanged" />
        </div>
        <div v-else></div>
    </div>
</template>

<script>
import axios from '@/services/axios';
import ListPagination from '@/components/ListPagination.vue';
import FilterGroups from '@/components/FilterGroups.vue';

export default {
    components: { ListPagination, FilterGroups },
    data() {
        return {
            realestates: [],
            currentPage: 1,     // mirrors ?page=
            totalItems: 0,
            limit: 20,
            searchTerm: '',     // mirrors ?search=
            activeFilters: [],
            lastRouteKey: '',   // prevents duplicate fetches

            filterGroups: [
                {
                    name: '',
                    multi: true,
                    filters: [
                        {
                            key: 'myRegion',
                            labelKey: 'browse.myRegion',
                            active: false,
                            activeClass: 'text-bg-dark',
                            inactiveClass: 'text-bg-light',
                            params: { user_region: '1' }
                        },
                        {
                            key: 'myProfileGroup',
                            labelKey: 'browse.myProfileGroup',
                            active: false,
                            activeClass: 'text-bg-dark',
                            inactiveClass: 'text-bg-light',
                            params: { user_profile_group: '1' }
                        }
                    ]
                }
            ]
        };
    },

    mounted() {
        // Load saved filters on first render (does NOT fetch)
        this.loadSelectedFilters();
    },

    watch: {
        // React to URL changes for page+search; this is the only place that fetches
        '$route.query': {
            immediate: true,
            deep: true,
            handler(newQ) {
                // normalize page
                const pRaw = newQ.page ?? '1';
                const p = parseInt(pRaw, 10);
                if (!Number.isFinite(p) || p < 1) {
                    this.$router.replace({ query: { ...newQ, page: '1' } });
                    return; // wait for normalized route
                }

                // normalize search
                const s = (newQ.search ?? '').toString();

                // keep input synced with URL
                if (this.searchTerm !== s) this.searchTerm = s;

                const key = `${p}|${s}`;
                if (key === this.lastRouteKey) return; // avoid duplicate fetches
                this.lastRouteKey = key;

                this.currentPage = p;
                this.fetchRealestates(p, s);
            }
        }
    },

    methods: {
        // Search submit -> update URL (page=1), watcher will fetch
        onSearchSubmit() {
            const term = (this.searchTerm || '').trim();
            const q = { ...this.$route.query, page: '1' };
            if (term) q.search = term; else delete q.search;
            this.$router.push({ query: q });
        },

        // Filters changed -> reset to page 1 (URL), then fetch once (keep search)
        onFiltersChanged(activeFilters) {
            this.activeFilters = activeFilters;
            const q = { ...this.$route.query, page: '1' };
            this.$router.push({ query: q });
            this.saveSelectedFilters();
            this.fetchRealestates(1, this.searchTerm);
            // Do NOT fetch here; watcher will fetch with new page
        },

        // Pagination -> update URL, watcher will fetch
        onPageChanged(newPage) {
            const q = { ...this.$route.query, page: String(newPage) };
            if (q.page !== this.$route.query.page) {
                this.$router.push({ query: q });
            }
        },

        async fetchRealestates(page = 1, searchTerm = '') {
            try {
                let url = `/realestates?page=${page}&limit=${this.limit}&search=${encodeURIComponent(searchTerm)}`;
                
                this.loadSelectedFilters();
                // merge active filter params (not in URL, only in request)
                const mergedParams = this.mergeFilterParams(this.activeFilters);
                Object.keys(mergedParams).forEach((key) => {
                    url += `&${key}=${encodeURIComponent(mergedParams[key])}`;
                });

                const response = await axios.get(url);
                this.realestates = response.data.data;
                this.totalItems = response.data.total;
                this.currentPage = page;
            } catch (error) {
                console.error('Error fetching realestates:', error);
            }
        },

        mergeFilterParams(filters) {
            const paramMap = {};
            filters.forEach((f) => {
                Object.entries(f.params).forEach(([key, rawValue]) => {
                    const values = String(rawValue).split(',');
                    (paramMap[key] ||= []).push(...values);
                });
            });

            const merged = {};
            Object.keys(paramMap).forEach((key) => {
                merged[key] = [...new Set(paramMap[key])].join(',');
            });
            return merged;
        },

        saveSelectedFilters() {
            const activeKeys = this.activeFilters.map((f) => f.key);
            localStorage.setItem('RealestateListFilterKeys', JSON.stringify(activeKeys));
        },

        loadSelectedFilters() {
            const stored = localStorage.getItem('RealestateListFilterKeys');
            if (!stored) return;
            try {
                const keysArray = JSON.parse(stored);
                this.filterGroups.forEach((group) => {
                    group.filters.forEach((filter) => {
                        filter.active = keysArray.includes(filter.key);
                    });
                });
            } catch (err) {
                console.warn('Could not parse saved filter keys:', err);
            }
            this.collectActiveFilters();
        },

        collectActiveFilters() {
            const active = [];
            this.filterGroups.forEach((group) => {
                group.filters.forEach((filter) => {
                    if (filter.active) active.push(filter);
                });
            });
            this.activeFilters = active;
        }
    }
};
</script>
